<script setup lang="ts">
import { ref, computed } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import RuleModelVersionDTO = App.Data.RuleEngine.RuleModelVersionDTO;
import RuleModelVersionMinimalDTO = App.Data.RuleEngine.RuleModelVersionMinimalDTO;
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import dayjs from "dayjs";
import { useBaseStore } from "@/stores/base";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const model_uuid: string | null =
	useRoute().params.model_uuid !== ""
		? String(useRoute().params.model_uuid)
		: null;

const itemsPerPage = ref<number>(100);

const headers = computed(() => [
	{ title: t("pqa.rules.versions.headers.name"), key: "name", sortable: true },
	{ title: t("pqa.rules.versions.headers.uuid"), key: "uuid", sortable: true },
	{
		title: t("pqa.rules.versions.headers.createdByUser"),
		key: "created_by_user",
		sortable: false,
	},
	{
		title: t("pqa.rules.versions.headers.createdAt"),
		key: "created_at",
		sortable: false,
	},
	{
		title: t("pqa.rules.versions.headers.isActive"),
		key: "is_active",
		sortable: false,
	},
	{
		title: t("pqa.rules.versions.headers.actions"),
		key: "actions",
		width: "150",
		align: "end",
	},
]);

const isLoading = ref<boolean>(false);
const items = ref<RuleModelVersionMinimalDTO[]>([]);
const totalItemCount = ref<number>(0);
const page = ref<number>(1);
const sortBy = ref([]);

const loadItems = debounce(async ({ page, itemsPerPage, sortBy }) => {
	isLoading.value = true;

	const response = await useApiClient("ruleEngine").versionIndex(model_uuid, {
		page,
		itemsPerPage,
		sortBy,
	});

	if (response.isOk()) {
		items.value = response.value;
		totalItemCount.value = response.value.length;
	}

	isLoading.value = false;
}, 300);

const makeActiveVersion = async (ruleModelVersion: RuleModelVersionDTO) => {
	const confirm = await useDialog().confirm({
		title: t("pqa.rules.versions.makeActive.title"),
		message: t("pqa.rules.versions.makeActive.message"),
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("ruleEngine").makeActiveVersion(
			model_uuid,
			ruleModelVersion.uuid
		)
	);

	if (response.isOk()) {
		loadItems({
			page: page.value,
			itemsPerPage: itemsPerPage.value,
			sortBy: sortBy.value,
		});
	}
};

const selectedDraftVersions = ref([]);

const destroyVersions = async () => {
	const confirm = await useDialog().confirm({
		title: t("pqa.rules.versions.destroy.title", {
			count: selectedDraftVersions.value.length,
		}),
		message: t("pqa.rules.versions.destroy.message", {
			count: selectedDraftVersions.value.length,
		}),
		isDelete: true,
	});
	if (!confirm) return;

	const response = await toastApiResult(
		useApiClient("ruleEngine").destroyVersions(
			model_uuid,
			selectedDraftVersions.value.map((draftVersion) => draftVersion.uuid)
		)
	);

	if (response.isOk()) {
		loadItems({
			page: page.value,
			itemsPerPage: itemsPerPage.value,
			sortBy: sortBy.value,
		});
	}
};

const isDeleteAvailable = computed(() => {
	return selectedDraftVersions.value.every((v) => !v.is_active && !v.was_active);
});

const userCanPublishRuleModel = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_publish_rule_model"
	);
});
</script>

<template>
	<v-card>
		<v-card-text>
			<v-data-table-server
				v-model="selectedDraftVersions"
				v-model:items-per-page="itemsPerPage"
				v-model:page="page"
				v-model:sort-by="sortBy"
				:headers="headers"
				:items="items"
				:items-length="totalItemCount"
				:loading="isLoading"
				@update:options="loadItems"
				return-object
				show-select
			>
				<template v-slot:item.name="{ item }">
					<router-link
						class="router-link-primary"
						:to="{
							name: 'pqa.rules.builder',
							params: {
								version_uuid: item.uuid,
								model_uuid: item.rule_model_uuid,
							},
						}"
					>
						{{ item.name }}
					</router-link>
				</template>
				<template v-slot:item.uuid="{ item }">
					<v-chip
						v-tooltip="`${item.uuid}`"
						density="compact"
						variant="tonal"
						color="on-surface"
					>
						...{{ item.uuid.slice(-5) }}
					</v-chip>
				</template>
				<template v-slot:item.created_by_user="{ item }">
					{{
						item.created_by_user?.name ??
						t("pqa.rules.versions.createdByAutomation")
					}}
				</template>
				<template v-slot:item.created_at="{ item }">
					{{ dayjs(item.created_at).format("MMMM D, YYYY - HH:mm:ss") }}
				</template>
				<template v-slot:item.is_active="{ item }">
					<v-chip v-if="item.is_active" color="primary" density="compact">
						<v-icon size="15" icon="fa: fa-light fa-check" start></v-icon>
						{{ t("pqa.rules.versions.status.active") }}
					</v-chip>
					<v-chip
						v-else-if="item.was_active"
						density="compact"
						variant="tonal"
						color="on-surface"
					>
						{{ t("pqa.rules.versions.status.retired") }}
					</v-chip>
					<v-chip v-else variant="tonal" color="on-surface" density="compact">
						{{ t("pqa.rules.versions.status.draft") }}
					</v-chip>
				</template>
				<template v-slot:item.actions="{ item }">
					<v-btn
						v-if="userCanPublishRuleModel"
						v-tooltip="t('pqa.rules.versions.makeActive.tooltip')"
						icon
						variant="text"
						:disabled="item.is_active"
						@click="makeActiveVersion(item)"
						density="compact"
					>
						<v-icon size="18">fa: fa-duotone fa-rotate</v-icon>
					</v-btn>
					<span v-else>-</span>
				</template>
				<template #footer.prepend>
					<div>
						<v-btn
							variant="tonal"
							prepend-icon="fa: fa-light fa-trash-alt"
							color="primary"
							:disabled="selectedDraftVersions.length === 0 || !isDeleteAvailable"
							@click="destroyVersions()"
						>
							{{ t("pqa.rules.versions.destroy.submit") }}
						</v-btn>
					</div>
					<v-spacer />
				</template>
			</v-data-table-server>
		</v-card-text>
	</v-card>
</template>

<style scoped></style>